
import React, { useState, useEffect, useMemo } from 'react';
import { getWorkingDaysData, updateWorkingDaysData, updateWorkingDaysSalesTax } from '../../../api/finance-api';
import TanstackTable from '../../../components/TanstackTable';
import { ColumnDef } from "@tanstack/react-table";
import Breadcrums from "../../../components/breadcrums";
import PageTitle from "../../../components/pageTitle";
import { sectionClass } from "../../../constants/common";
import logger from "../../../utils/logger";

interface WorkingDaysData {
    uuid: string;
    month_year: string;
    working_days: number;
    employee_name: string;
    sales_tax?: number;
}

const WorkingDays: React.FC = () => {
    const [data, setData] = useState<WorkingDaysData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editRow, setEditRow] = useState<{ uuid: string; month_year: string } | null>(null);
    const [editValue, setEditValue] = useState<number>(0);
    const [editSalesTaxRow, setEditSalesTaxRow] = useState<{ uuid: string; month_year: string } | null>(null);
    const [editSalesTaxValue, setEditSalesTaxValue] = useState<string>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getWorkingDaysData();
            setData(res || []);
        } catch (e: any) {
            setError(e?.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (row: WorkingDaysData) => {
        setEditRow({ uuid: row.uuid, month_year: row.month_year });
        setEditValue(row.working_days);
    };

    const handleCancel = () => {
        setEditRow(null);
        setEditValue(0);
    };

    const handleEditSalesTax = (row: WorkingDaysData) => {
        setEditSalesTaxRow({ uuid: row.uuid, month_year: row.month_year });
        setEditSalesTaxValue(row.sales_tax != null ? String(row.sales_tax) : '');
    };

    const handleCancelSalesTax = () => {
        setEditSalesTaxRow(null);
        setEditSalesTaxValue('');
    };

    const handleSaveSalesTax = async () => {
        if (!editSalesTaxRow) return;
        const salesTaxValue = editSalesTaxValue === '' ? 0 : Number(editSalesTaxValue);
        setSaving(true);
        setError(null);
        try {
            const resp = await updateWorkingDaysSalesTax(editSalesTaxRow.uuid, editSalesTaxRow.month_year, salesTaxValue);
            console.log('API update response:', resp);
            if (resp && resp.affectedRows === 0) {
                setError('No rows updated. Please check if the record exists.');
                setSaving(false);
                return;
            }
            setData(prev => prev.map(item =>
                item.uuid === editSalesTaxRow.uuid && item.month_year === editSalesTaxRow.month_year
                    ? { ...item, sales_tax: salesTaxValue || undefined }
                    : item
            ));
            setEditSalesTaxRow(null);
        } catch (e: any) {
            logger.error('Update error:', e);
            setError(e?.message || 'Failed to update');
        } finally {
            setSaving(false);
        }
    };

        const handleSave = async () => {
            if (!editRow) return;
            setSaving(true);
            setError(null);
            try {
                const resp = await updateWorkingDaysData(editRow.uuid, editRow.month_year, editValue);
                console.log('API update response:', resp);
                if (resp && resp.affectedRows === 0) {
                    setError('No rows updated. Please check if the record exists.');
                    setSaving(false);
                    return;
                }
                setData(prev => prev.map(item =>
                    item.uuid === editRow.uuid && item.month_year === editRow.month_year
                        ? { ...item, working_days: editValue }
                        : item
                ));
                setEditRow(null);
            } catch (e: any) {
                logger.error('Update error:', e);
                setError(e?.message || 'Failed to update');
            } finally {
                setSaving(false);
            }
        };

    const columns = useMemo<ColumnDef<WorkingDaysData, any>[]>(() => [
        {
            accessorKey: 'employee_name',
            header: 'Employee Name',
            cell: ({ row }) => <span className="font-semibold">{row.original.employee_name || 'Unknown'}</span>,
            enableColumnFilter: true,
            enableGrouping: false,
            meta: { cellClassName: "text-left" },
            footer: ({ table }) => (
                <div>
                    <span className="font-semibold text-md">Total</span>
                    <br />
                    <span className="text-xs text-gray-600">{table.getFilteredRowModel().rows.length} records</span>
                </div>
            ),
        },
        {
            accessorKey: 'month_year',
            header: 'Month-Year',
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            enableGrouping: false,
            meta: { cellClassName: "text-left" },
            footer: () => null,
        },
        {
            accessorKey: 'working_days',
            header: 'Working Days',
            size: 180,
            cell: ({ row }) => {
                const isEditing = editRow && row.original.uuid === editRow.uuid && row.original.month_year === editRow.month_year;
                if (isEditing) {
                    return (
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min="0"
                                max="31"
                                value={editValue}
                                onChange={e => setEditValue(Number(e.target.value))}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                                disabled={saving}
                            />
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                            >{saving ? '...' : '✓'}</button>
                            <button
                                onClick={handleCancel}
                                disabled={saving}
                                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 disabled:opacity-50"
                            >✕</button>
                        </div>
                    );
                }
                return (
                    <div className="flex items-center justify-between">
                        <span>{row.original.working_days}</span>
                        <button
                            onClick={() => handleEdit(row.original)}
                                    className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                >Edit</button>
                                </div>
                            );
                        },
                        enableGrouping: false,
                        enableColumnFilter: false,
                        meta: { cellClassName: "text-right" },
                        footer: ({ table }) => (
                <span className="font-semibold">
                    {table.getFilteredRowModel().rows.reduce((total, row) => total + (Number(row.original.working_days) || 0), 0)}
                </span>
            ),
        },
        {
            accessorKey: 'sales_tax',
            header: 'Sales Tax %',
            size: 180,
            cell: ({ row }) => {
                const isEditingSalesTax = editSalesTaxRow && row.original.uuid === editSalesTaxRow.uuid && row.original.month_year === editSalesTaxRow.month_year;
                if (isEditingSalesTax) {
                    return (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                inputMode="decimal"
                                value={editSalesTaxValue}
                                onChange={e => setEditSalesTaxValue(e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                                disabled={saving}
                            />
                            <button
                                onClick={handleSaveSalesTax}
                                disabled={saving}
                                className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                            >{saving ? '...' : '✓'}</button>
                            <button
                                onClick={handleCancelSalesTax}
                                disabled={saving}
                                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 disabled:opacity-50"
                            >✕</button>
                        </div>
                    );
                }
                return (
                    <div className="flex items-center justify-between gap-2">
                        <span>{row.original.sales_tax != null ? `${row.original.sales_tax}%` : '-'}</span>
                        <button
                            onClick={() => handleEditSalesTax(row.original)}
                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                        >{row.original.sales_tax != null ? 'Edit' : 'Add'}</button>
                    </div>
                );
            },
            enableGrouping: false,
            enableColumnFilter: false,
            meta: { cellClassName: "text-right" },
            footer: () => null,
        },
    ], [editRow, editValue, editSalesTaxRow, editSalesTaxValue, saving]);

    return (
        <main>
            <PageTitle title="Working Days" />
            <Breadcrums />
            <section className={sectionClass}>
            {loading && <div className="my-5">Loading working days data...</div>}
            {error && <div className="my-5 text-red-600">Error: {error}</div>}
            {/* Debug info for troubleshooting */}
            {/* <pre>{JSON.stringify({ editRow, editValue, saving }, null, 2)}</pre> */}
                {!loading && !error && data.length > 0 && (
                    <TanstackTable data={data} columns={columns} downloadBtn={true} />
                )}
                {!loading && !error && data.length === 0 && (
                    <div className="my-5">No working days records found</div>
                )}
            </section>
        </main>
    );
};

export default WorkingDays;